import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [dbApproved, setDbApproved] = useState<boolean | null>(null);

  const jwtApproved = (user?.app_metadata as { approved?: boolean } | undefined)?.approved === true;

  useEffect(() => {
    if (loading || !user || jwtApproved) {
      setDbApproved(null);
      return;
    }
    let cancelled = false;
    supabase
      .rpc('get_my_approval_status')
      .then(({ data, error }) => {
        if (cancelled || error) {
          if (!cancelled) setDbApproved(false);
          return;
        }
        setDbApproved((data as { approved?: boolean } | null)?.approved ?? false);
      })
      .catch(() => {
        if (!cancelled) setDbApproved(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run on user id / loading / jwt; omit full user to avoid ref churn
  }, [user?.id, loading, jwtApproved]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wt-bg-primary">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (jwtApproved) {
    // JWT has approved; no need to check DB.
  } else if (dbApproved === true) {
    // JWT missing approved but DB says approved (e.g. hook not enabled); allow through.
  } else if (dbApproved === false) {
    return <Navigate to="/pending-approval" replace />;
  } else {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wt-bg-primary">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Checking access...</p>
        </div>
      </div>
    );
  }

  if (user.user_metadata?.terms_accepted !== true) {
    return <Navigate to="/accept-terms" replace />;
  }

  return <>{children}</>;
}
